import React, { useState, useEffect, useRef } from 'react';

interface CurrencyInputProps {
    id: string;
    name: string;
    label: string;
    value: string | number | null | undefined;
    onValueChange: (value: string | undefined, name: string | undefined, values: any) => void;
    disabled?: boolean;
    placeholder?: string;
    prefix?: string;
    allowNegativeValue?: boolean;
    decimalsLimit?: number;
    error?: string;
    'data-testid'?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
    id,
    name,
    label,
    value,
    onValueChange,
    disabled = false,
    placeholder = '0,00',
    prefix = 'R$',
    allowNegativeValue = false,
    decimalsLimit = 2,
    error,
    'data-testid': dataTestId,
}) => {
    const [displayValue, setDisplayValue] = useState('');
    const isTypingRef = useRef(false);

    // Função para limpar a entrada deixando apenas números e vírgula
    const cleanValue = (val: string): string => {
        return val.replace(/[^\d,]/g, '');
    };

    // Função para formatar o valor no padrão brasileiro
    const formatBrazilianCurrency = (val: string): string => {
        if (!val) return '';

        const cleaned = cleanValue(val);
        if (!cleaned) return '';

        // Se contém vírgula, trata como decimal
        if (cleaned.includes(',')) {
            const [integerPart, decimalPart = ''] = cleaned.split(',');

            // Limita casas decimais mas NÃO adiciona zeros durante digitação
            const limitedDecimal = decimalPart.slice(0, decimalsLimit);

            // Remove zeros à esquerda da parte inteira
            const cleanInteger = integerPart.replace(/^0+/, '') || '0';

            // Formata a parte inteira com separadores de milhares
            const formattedInteger = parseInt(cleanInteger, 10).toLocaleString('pt-BR');

            // Retorna sem adicionar zeros - deixa o usuário digitar naturalmente
            return `${formattedInteger},${limitedDecimal}`;
        } else {
            // Sem vírgula, trata como número inteiro
            const numericValue = parseInt(cleaned, 10);
            if (isNaN(numericValue)) return '';

            return numericValue.toLocaleString('pt-BR');
        }
    };

    // Função para converter o valor formatado para número
    const parseToNumber = (formattedValue: string): number => {
        if (!formattedValue) return 0;

        const cleaned = cleanValue(formattedValue);
        if (!cleaned) return 0;

        if (cleaned.includes(',')) {
            const [integerPart, decimalPart = ''] = cleaned.split(',');
            const integer = parseInt(integerPart.replace(/^0+/, '') || '0', 10);

            // Calcular decimal baseado no que o usuário realmente digitou
            const decimalValue = decimalPart ? parseFloat(`0.${decimalPart}`) : 0;

            return integer + decimalValue;
        } else {
            return parseInt(cleaned, 10) || 0;
        }
    };

    // Inicializar o valor quando o prop value muda (APENAS se não estiver digitando)
    useEffect(() => {
        // Não sobrescrever se o usuário está digitando
        if (isTypingRef.current) {
            return;
        }

        if (value !== null && value !== undefined && value !== '') {
            const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;

            // Se é um número inteiro, não mostra decimais
            if (numValue % 1 === 0) {
                setDisplayValue(numValue.toLocaleString('pt-BR'));
            } else {
                // Se tem decimais, mostra com vírgula
                setDisplayValue(numValue.toLocaleString('pt-BR', {
                    minimumFractionDigits: decimalsLimit,
                    maximumFractionDigits: decimalsLimit
                }));
            }
        } else {
            setDisplayValue('');
        }
    }, [value, decimalsLimit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Marcar que o usuário está digitando
        isTypingRef.current = true;

        // Permite deletar tudo
        if (!inputValue) {
            setDisplayValue('');
            onValueChange('', name, {
                float: 0,
                formatted: '',
                value: '0'
            });
            // Reset flag após um pequeno delay
            setTimeout(() => {
                isTypingRef.current = false;
            }, 100);
            return;
        }

        // Formatar o valor
        const formatted = formatBrazilianCurrency(inputValue);
        setDisplayValue(formatted);

        // Converter para número e chamar callback
        const numericValue = parseToNumber(formatted);

        onValueChange(formatted, name, {
            float: numericValue,
            formatted: formatted,
            value: numericValue.toString()
        });

        // Reset flag após um pequeno delay para permitir que o useEffect seja chamado normalmente
        setTimeout(() => {
            isTypingRef.current = false;
        }, 100);
    };

    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-700">
                {label}
            </label>
            <div className="mt-1">
                <div className="flex rounded-lg">
                    <span className="px-4 inline-flex items-center min-w-fit rounded-s-md border border-e-0 border-gray-200 bg-gray-50 text-sm text-gray-500">
                        {prefix}
                    </span>
                    <input
                        id={id}
                        name={name}
                        type="text"
                        value={displayValue}
                        onChange={handleChange}
                        disabled={disabled}
                        placeholder={placeholder}
                        className="py-1.5 sm:py-2 px-3 block w-full border-gray-200 rounded-e-lg text-sm focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        data-testid={dataTestId}
                        autoComplete="off"
                    />
                </div>
                {error && (
                    <p className="mt-1 text-sm text-red-600">{error}</p>
                )}
            </div>
        </div>
    );
};

export default CurrencyInput; 